import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Star, Image, Film, Ticket, Share2, Users, ArrowRight, Copy, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 20 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

export default function EventLandingPage() {
  const { eventSlug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/events/${eventSlug}`).then(r => setEvent(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [eventSlug]);

  const shareUrl = window.location.href;

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => { setCopied(true); toast.success("Link copied!"); setTimeout(() => setCopied(false), 2000); });
  };

  const shareWhatsApp = () => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out ${event?.title} - ${shareUrl}`)}`, "_blank");
  const shareInstagram = () => { copyLink(); toast.info("Link copied! Paste it in your Instagram story or bio."); };

  if (loading) return <div className="min-h-screen bg-luxury-black flex items-center justify-center"><div className="text-gold animate-pulse font-body">Loading...</div></div>;
  if (!event) return <div className="min-h-screen bg-luxury-black flex items-center justify-center"><p className="text-white/40 font-body">Event not found</p></div>;

  const images = event.images || [];
  const reviews = event.reviews || [];

  return (
    <div data-testid="event-landing-page" className="min-h-screen bg-luxury-black">
      {/* Full-width Hero */}
      {event.cover_image && (
        <div className="relative h-[60vh] overflow-hidden">
          <img src={event.cover_image} alt={event.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-luxury-black via-luxury-black/40 to-transparent" />
          <div className="absolute top-6 right-6 z-10 flex gap-2">
            <button onClick={copyLink} data-testid="share-copy" className="flex items-center gap-2 bg-black/60 backdrop-blur-sm text-white/70 hover:text-gold px-4 py-2 text-xs font-body transition-colors border border-white/10">
              {copied ? <CheckCircle size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy Link"}
            </button>
            <button onClick={shareWhatsApp} data-testid="share-whatsapp" className="flex items-center gap-2 bg-green-600/80 backdrop-blur-sm text-white px-4 py-2 text-xs font-body hover:bg-green-500 transition-colors">
              <Share2 size={14} /> WhatsApp
            </button>
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16 max-w-5xl mx-auto">
            <span className="px-4 py-1.5 bg-gold/20 text-gold text-[10px] font-body uppercase tracking-[0.2em]">{event.event_type}</span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light text-white mt-4 leading-[1.1]" data-testid="landing-event-title">{event.title}</h1>
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-12">
        <motion.div initial="hidden" animate="visible">
          {/* Event Info */}
          <motion.div variants={fadeUp} custom={0} className="flex flex-wrap items-center gap-6 mb-8 text-white/40 text-sm font-body">
            {event.event_date && <span className="flex items-center gap-2"><Calendar size={14} className="text-gold" /> {event.event_date}</span>}
            {event.location && <span className="flex items-center gap-2"><MapPin size={14} className="text-gold" /> {event.location}</span>}
            {event.attendance > 0 && <span className="flex items-center gap-2"><Users size={14} className="text-gold" /> {event.attendance} attended</span>}
            {event.avg_rating > 0 && <span className="flex items-center gap-2"><Star size={14} className="text-gold fill-gold" /> {event.avg_rating}/5 ({event.review_count} reviews)</span>}
          </motion.div>

          {event.description && <motion.p variants={fadeUp} custom={1} className="text-white/60 font-body font-light text-lg leading-relaxed mb-12 max-w-3xl">{event.description}</motion.p>}

          {/* CTA */}
          <motion.div variants={fadeUp} custom={2} className="flex flex-wrap gap-4 mb-16">
            <Link to={`/events/${eventSlug}`} data-testid="landing-view-full" className="inline-flex items-center gap-2 bg-gold text-black font-body font-medium text-sm px-8 py-4 hover:bg-gold-light transition-colors">
              <Ticket size={16} /> Get Tickets & View Full Event <ArrowRight size={16} />
            </Link>
            <Link to={`/events/${eventSlug}/checkin`} data-testid="landing-checkin" className="inline-flex items-center gap-2 border border-gold/30 text-gold font-body text-sm px-8 py-4 hover:bg-gold/10 transition-colors">
              Check In
            </Link>
          </motion.div>

          {/* Photo Preview */}
          {images.length > 0 && (
            <motion.div variants={fadeUp} custom={3} className="mb-16">
              <h2 className="font-heading text-2xl text-white mb-6 flex items-center gap-2"><Image size={20} className="text-gold" /> Gallery Preview</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {images.slice(0, 8).map((img, i) => (
                  <div key={img.id} className="aspect-square overflow-hidden">
                    <img src={img.url} alt={img.filename} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                ))}
              </div>
              {images.length > 8 && (
                <Link to={`/events/${eventSlug}`} className="inline-flex items-center gap-2 text-gold/60 hover:text-gold font-body text-sm mt-4 transition-colors">
                  View all {images.length} photos <ArrowRight size={14} />
                </Link>
              )}
            </motion.div>
          )}

          {/* Reviews Preview */}
          {reviews.length > 0 && (
            <motion.div variants={fadeUp} custom={4} className="mb-16">
              <h2 className="font-heading text-2xl text-white mb-6 flex items-center gap-2"><Star size={20} className="text-gold" /> Guest Reviews</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {reviews.slice(0, 4).map(r => (
                  <div key={r.id} className="glass-card p-6">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-white font-body text-sm font-medium">{r.reviewer_name}</p>
                      <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, j) => <Star key={j} size={12} className={j < r.overall_rating ? "text-gold fill-gold" : "text-white/15"} />)}</div>
                    </div>
                    {r.liked && <p className="text-white/50 font-body text-xs">{r.liked}</p>}
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Share Footer */}
          <motion.div variants={fadeUp} custom={5} className="text-center py-10 border-t border-white/5">
            <p className="text-white/30 text-xs font-body uppercase tracking-wider mb-4">Share this event</p>
            <div className="flex items-center justify-center gap-3">
              <button onClick={copyLink} className="flex items-center gap-2 border border-white/10 text-white/40 hover:text-gold hover:border-gold/30 px-5 py-2 text-xs font-body transition-colors"><Copy size={14} /> Copy Link</button>
              <button onClick={shareWhatsApp} className="flex items-center gap-2 bg-green-600/20 text-green-400 px-5 py-2 text-xs font-body hover:bg-green-600/30 transition-colors"><Share2 size={14} /> WhatsApp</button>
              <button onClick={shareInstagram} className="flex items-center gap-2 bg-pink-600/20 text-pink-400 px-5 py-2 text-xs font-body hover:bg-pink-600/30 transition-colors"><Share2 size={14} /> Instagram</button>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
